import { useState, useCallback } from 'react';
import { orderService, handleApiError, Order, CreateOrderFromCartRequest, UpdateOrderRequest, OrderListParams } from '../services';
import { trackEvent } from '../services/analytics';

export interface UseOrdersReturn {
  orders: Order[];
  currentOrder: Order | null;
  isLoading: boolean;
  error: string | null;
  total: number;
  hasMore: boolean;
  loadOrders: (params?: OrderListParams) => Promise<void>;
  loadMoreOrders: () => Promise<void>;
  createOrderFromCart: (orderData: CreateOrderFromCartRequest) => Promise<Order | null>;
  loadOrderById: (orderId: string) => Promise<void>;
  updateOrder: (orderId: string, updateData: UpdateOrderRequest) => Promise<void>;
  updateOrderStatus: (orderId: string, status: Order['status']) => Promise<void>;
  getOrdersByStatus: (status: Order['status']) => Promise<void>;
  clearError: () => void;
}

export const useOrders = (): UseOrdersReturn => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [currentParams, setCurrentParams] = useState<OrderListParams & { hasMoreRecords?: boolean }>({});

  const loadOrders = useCallback(async (params: OrderListParams = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await orderService.getOrders({
        limit: params.limit || 20,
        offset: params.offset || 0,
        status_filter: params.status_filter,
        source: params.source,
      });
      
      // Backend returns an array directly
      const orders = response as Order[];
      
      // For pagination: if we got fewer orders than requested limit, we've reached the end
      const requestedLimit = params.limit || 20;
      const hasMoreRecords = orders.length === requestedLimit;
      
      const isNewSearch = (params.offset || 0) === 0;
      
      if (isNewSearch) {
        // New search/filter - replace orders completely
        setOrders(orders);
        setTotal(orders.length);
      } else {
        // Load more - append orders
        setOrders(prev => {
          const newOrders = [...prev, ...orders];
          setTotal(newOrders.length);
          return newOrders;
        });
      }
      
      setCurrentOffset(params.offset || 0);
      setCurrentParams({ ...params, hasMoreRecords });
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadMoreOrders = useCallback(async () => {
    if (isLoading) return;
    
    const nextOffset = currentOffset + (currentParams.limit || 20);
    await loadOrders({
      ...currentParams,
      offset: nextOffset,
    });
  }, [isLoading, currentOffset, currentParams, loadOrders]);

  const createOrderFromCart = useCallback(async (orderData: CreateOrderFromCartRequest): Promise<Order | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const newOrder = await orderService.createOrderFromCart(orderData);
      // Add new order to the beginning of the list
      setOrders(prev => [newOrder, ...prev]);
      setCurrentOrder(newOrder);
      // GA4 standard e-commerce `purchase` event -- required name +
      // shape for the built-in Monetization / Revenue reports to
      // pick this up. transaction_id, value, currency, items are all
      // GA4 spec fields; adding domain identifiers (order_number)
      // as extra props for our own reports.
      // https://developers.google.com/analytics/devguides/collection/ga4/reference/events#purchase
      const currency = newOrder.currency || 'IDR';
      trackEvent('purchase', {
        transaction_id: newOrder.order_number || newOrder.id,
        value: newOrder.total_amount,
        currency,
        order_id: newOrder.id,
        order_number: newOrder.order_number,
        item_count: newOrder.items?.length,
        items: (newOrder.items || []).map((it) => ({
          item_id: it.product_id,
          item_name: it.product_name,
          price: it.unit_price,
          quantity: it.quantity,
          currency,
        })),
      });
      return newOrder;
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
      setIsLoading(false);
      return null;
    }
  }, []);

  const loadOrderById = useCallback(async (orderId: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const order = await orderService.getOrderById(orderId);
      setCurrentOrder(order);
      
      // Update order in the list if it exists
      setOrders(prev => 
        prev.map(o => o.id === orderId ? order : o)
      );
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateOrder = useCallback(async (orderId: string, updateData: UpdateOrderRequest) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const updatedOrder = await orderService.updateOrder(orderId, updateData);
      
      // Update order in the list
      setOrders(prev => 
        prev.map(o => o.id === orderId ? updatedOrder : o)
      );
      
      // Update current order if it's the same one
      if (currentOrder?.id === orderId) {
        setCurrentOrder(updatedOrder);
      }
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [currentOrder]);

  const updateOrderStatus = useCallback(async (orderId: string, status: Order['status']) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const updatedOrder = await orderService.updateOrderStatus(orderId, { status });
      
      // Update order in the list
      setOrders(prev => 
        prev.map(o => o.id === orderId ? updatedOrder : o)
      );
      
      // Update current order if it's the same one
      if (currentOrder?.id === orderId) {
        setCurrentOrder(updatedOrder);
      }
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [currentOrder]);

  const getOrdersByStatus = useCallback(async (status: Order['status']) => {
    await loadOrders({ status_filter: status, offset: 0 });
  }, [loadOrders]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const hasMore = currentParams.hasMoreRecords || false;

  return {
    orders,
    currentOrder,
    isLoading,
    error,
    total,
    hasMore,
    loadOrders,
    loadMoreOrders,
    createOrderFromCart,
    loadOrderById,
    updateOrder,
    updateOrderStatus,
    getOrdersByStatus,
    clearError,
  };
};